<template>
  <div class="mt-2">
    <!-- <div :style="[!noInnerWidth ? { width: width } : {}]"> -->
    <div :style="{ width: !noInnerWidth ? width : 'auto' }">
      <!-- Riepilogo del periodo: saldo iniziale, entrate, uscite e saldo
           finale. Gli stessi valori sono nella prima e nelle ultime righe
           della tabella. -->
      <div v-if="hasSummary" class="balance-summary">
        <div class="balance-summary__tile">
          <span class="balance-summary__label"
            >Saldo iniziale al {{ toLocaleDate(previous_balance_date) }}</span
          >
          <strong class="balance-summary__value">{{
            toLocaleCurrency(openingBalance)
          }}</strong>
        </div>
        <div class="balance-summary__tile">
          <span class="balance-summary__label">Entrate</span>
          <strong class="balance-summary__value is-positive"
            >+ {{ toLocaleCurrency(Math.abs(current_balance.entrate)) }}</strong
          >
        </div>
        <div class="balance-summary__tile">
          <span class="balance-summary__label">Uscite</span>
          <strong class="balance-summary__value is-negative"
            >− {{ toLocaleCurrency(Math.abs(current_balance.uscite)) }}</strong
          >
        </div>
        <div class="balance-summary__tile balance-summary__tile--final">
          <span class="balance-summary__label"
            >Saldo finale al {{ toLocaleDate(current_balance_date) }}</span
          >
          <strong class="balance-summary__value">{{
            toLocaleCurrency(totalBalance())
          }}</strong>
        </div>
      </div>
      <div>
        <b-table
          :busy="isLoading"
          head-variant="light"
          :no-local-sorting="true"
          :items="items"
          :fields="getAllFields"
          :current-page="currentPage"
          per-page="0"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :sort-direction="sortDirection"
          :sticky-header="height"
          no-border-collapse
          responsive
          show-empty
          empty-text="Non ci sono informazioni da mostrare"
          small
          @sort-changed="sortingChanged"
        >
          <template #table-busy>
            <table-skeleton />
          </template>
          <template #empty>
            <template v-if="newExpCashEntryBtn">
              <div
                class="d-flex flex-column justify-content-center align-items-center my-3"
                style="max-width: max-content; margin-inline: auto"
              >
                <p class="mb-2">
                  Effettua una ricerca oppure, se non trovi risultati, crea un
                  nuovo Estratto Conto cliccando sul pulsante qui sotto
                </p>
                <b-button
                  class="btn-create px-3"
                  type="button"
                  variant="primary"
                  size="sm"
                  @click="onAddNewExpCashEntry"
                  title="Crea"
                  :disabled="!canVerb(resource, 'store')"
                  style="max-width: max-content"
                >
                  <b-icon icon="plus-circle" aria-hidden="true"></b-icon>
                  Crea
                </b-button
                >
              </div>
            </template>
            <template v-else>
              <empty-table
                :isTableInModal="isTableInModal"
                :performedSearch="performedSearch"
                :automaticPerformedSearch="automaticPerformedSearch"
                :filterCount="filterCount"
              />
            </template
            >
          </template>
          <template #cell(cash_reference_full_code)="{ item }">
            {{ item.causale.codice }} - {{ item.causale.descrizione }}
          </template>
          <template #cell(entry_in)="{ item }">
            {{
              item.causale.tipo_di_movimento === 0
                ? toLocaleCurrency(Math.abs(item.importo))
                : toLocaleCurrency(0)
            }}
          </template>
          <template #cell(entry_out)="{ item }">
            {{
              item.causale.tipo_di_movimento === 1
                ? toLocaleCurrency(Math.abs(item.importo))
                : toLocaleCurrency(0)
            }}
          </template>
          <template #cell(actions)="row">
            <div class="action-buttons">
              <b-button
                v-if="actions.includes('infomodal')"
                size="sm"
                variant="lisaweb"
                @click="openModal(row.item, row.index, $event.target)"
                class="mt-1 mr-1"
                title="Modale Dettagli"
              >
                <base-icon name="eye"></base-icon>
              </b-button>

              <b-button
                v-if="actions.includes('details')"
                size="sm"
                variant="lisaweb"
                @click="row.toggleDetails"
                class="mt-1 mr-1"
                :title="
                  row.detailsShowing ? 'Chiudi dettagli' : 'Apri dettagli'
                "
              >
                <b-icon v-if="row.detailsShowing" icon="chevron-up"></b-icon>
                <b-icon v-else icon="chevron-down"></b-icon>
              </b-button>

              <b-button
                v-if="actions.includes('edit')"
                size="sm"
                variant="lisaweb"
                :disabled="!isStandard(row.item)"
                @click="onEdit(row.item.id, row.item)"
                class="mt-1 mr-1"
                title="Modifica"
              >
                <base-icon name="edit"></base-icon>
              </b-button>
              <b-button
                v-if="actions.includes('destroy')"
                :disabled="!isStandard(row.item)"
                size="sm"
                variant="lisaweb"
                @click="onDestroy(row.item.id, row.item)"
                class="mt-1 mr-1"
                title="Elimina"
              >
                <base-icon name="trash"></base-icon>
              </b-button>
            </div>
          </template>
          <!-- Saldo iniziale in prima riga. -->
          <template #top-row="{ fields }" v-if="hasSummary">
            <td
              v-for="(field, i) in fields"
              :key="i"
              class="balance-row"
              :class="balanceCellClass(i, fields)"
            >
              <template v-if="i === labelIndex(fields)">
                Saldo iniziale al {{ toLocaleDate(previous_balance_date) }}
              </template>
              <template v-else-if="field.key === 'entrate'">
                {{ balanceIn(openingBalance) }}
              </template>
              <template v-else-if="field.key === 'uscite'">
                {{ balanceOut(openingBalance) }}
              </template>
            </td>
          </template>
          <!-- Totale dei movimenti e saldo finale in fondo. -->
          <template #custom-foot="{ fields }" v-if="hasSummary">
            <tr>
              <td
                v-for="(field, i) in fields"
                :key="i"
                class="totals-row"
                :class="balanceCellClass(i, fields)"
              >
                <template v-if="i === labelIndex(fields)">
                  Totale movimenti
                </template>
                <template v-else-if="field.key === 'entrate'">
                  {{ toLocaleCurrency(Math.abs(current_balance.entrate)) }}
                </template>
                <template v-else-if="field.key === 'uscite'">
                  {{ toLocaleCurrency(Math.abs(current_balance.uscite)) }}
                </template>
              </td>
            </tr>
            <tr>
              <td
                v-for="(field, i) in fields"
                :key="i"
                class="totals-row totals-row--final"
                :class="balanceCellClass(i, fields)"
              >
                <template v-if="i === labelIndex(fields)">
                  Saldo finale al {{ toLocaleDate(current_balance_date) }}
                </template>
                <template v-else-if="field.key === 'entrate'">
                  {{ balanceIn(totalBalance()) }}
                </template>
                <template v-else-if="field.key === 'uscite'">
                  {{ balanceOut(totalBalance()) }}
                </template>
              </td>
            </tr>
          </template>
          <template #cell()="{ value }">
            <span>{{ value }}</span>
          </template>
        </b-table>
      </div>
      <pagination-skeleton v-if="isLoading && !noPagination" />
      <div
        class="pagination-wrapper"
        v-if="!isLoading && !noPagination && items.length"
      >
        <div class="info">
          <div class="show-text">
            <p>
              <label for="per-page-select-exp-cash-entry"
                >Numero di risultati per pagina:</label
              >
            </p>
          </div>
          <div class="group">
            <b-form-group label-for="per-page-select">
              <b-form-select
                id="per-page-select-exp-cash-entry"
                v-model="perPage"
                :options="pageOptions"
                style="width: 68px"
                size="sm"
                @change="onPageChange(1)"
                :disabled="!items.length"
              ></b-form-select>
            </b-form-group>
          </div>
          <div class="page">
            <p>
              Risultati da
              <strong>{{ formatItemsFrom }}</strong> a
              <strong>{{ formatItemsTo }}</strong> su un totale di
              <strong>{{ formatTotalItems }}</strong>
            </p>
          </div>
        </div>
        <div class="info-action">
          <div class="pagination" v-if="totalItems">
            <b-pagination
              v-model="currentPage"
              limit="3"
              :total-rows="totalItems"
              :per-page="perPage"
              size="sm"
              @change="onPageChange"
            >
              <template #page="{ page, active }">
                <span :data-active="active">{{
                  page.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")
                }}</span>
              </template>
              <template v-slot:first-text>
                <span
                  ><base-icon
                    scale="-1"
                    name="chevron_right_last_rounded"
                    width="16"
                    height="16"
                /></span>
              </template>
              <template v-slot:prev-text>
                <span
                  ><base-icon
                    scale="-1"
                    name="chevron_right_rounded"
                    width="16"
                    height="16"
                /></span>
              </template>
              <template v-slot:next-text>
                <span
                  ><base-icon
                    name="chevron_right_rounded"
                    width="16"
                    height="16"
                /></span>
              </template>
              <template v-slot:last-text>
                <span
                  ><base-icon
                    name="chevron_right_last_rounded"
                    width="16"
                    height="16"
                /></span>
              </template>
            </b-pagination>
          </div>
          <div
            class="input_pagination"
            v-if="
              totalItems && !noInputPaginator && totalPages && totalPages > 3
            "
          >
            <base-input-pagination
              @pagination="onInputPagination"
            ></base-input-pagination>
          </div>
        </div>
      </div>
      <b-modal
        :id="infomodalName"
        @hidden="onModalHidden"
        :title="modalTitle"
        size="xl"
        scrollable
        ok-only
        ok-variant="lisaweb"
        header-bg-variant="lisaweb"
        no-close-on-backdrop
      >
        <div>
          <b-tabs content-class="pt-1" lazy v-model="tabIndex">
            <b-tab
              v-for="(tab, index) in tabs"
              :key="index"
              @click="setTab(tab, index)"
              :title="tab.text"
              :active="index === tabIndex"
              title-item-class="lisaweb"
            >
              <keep-alive>
                <component
                  v-if="selected === tab.name"
                  :is="selected"
                  :resourceId="modalResourceId"
                  @fetch="fetch"
                ></component>
              </keep-alive>
            </b-tab>
          </b-tabs>
        </div>
      </b-modal>
    </div>
  </div>
</template>

<script>
import template from "./template";
import General from "@/components/profile/companies-products/insurers/details/General";
import BaseIcon from "@/components/BaseIcon";
import BaseInputPagination from "@/components/form/BaseInputPagination";
import {toLocaleCurrency} from "@/utils/strings";
import {toLocaleDate} from "@/utils/dates";
import {mapActions} from "vuex";
import EmptyTable from "@/components/EmptyTable.vue";
import moment from "moment";

moment.locale("it");

export default {
  name: "CashEntriesTable",
  extends: template,
  data() {
    return {
      tabs: [{name: "General", text: "Generale"}],
      selected: "General",
      modalResourceId: null,
      tabIndex: 0,
    };
  },
  components: {
    General,
    BaseIcon,
    BaseInputPagination,
    EmptyTable,
  },
  mounted() {
    console.log("previous_balance", this.previous_balance);
  },
  computed: {
    // Saldi del periodo caricati (fetchSummary del componente padre).
    hasSummary() {
      return !!(
        this.items.length &&
        Object.keys(this.previous_balance).length &&
        Object.keys(this.current_balance).length
      );
    },
    openingBalance() {
      return parseFloat(this.previous_balance.saldo) || 0;
    },
    formatItemsFrom() {
      if (!this.totalItems) return "";
      return this.items_from.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },
    formatItemsTo() {
      if (!this.totalItems) return "";
      return this.items_to.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },
    formatTotalItems() {
      if (!this.totalItems) return "";
      return this.totalItems.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },
  },
  methods: {
    toLocaleCurrency,
    toLocaleDate,
    fetchPage(val) {
      this.$parent.currentPage = val;
      this.currentPage = val;
      let filter = this.keepFilter();
      this.$parent.fetch(this.$parent.filterName).then(() => {
        if (filter) {
          let name = this.filterName;
          this.removeFilterByName(name);
          this.removePaginationByName(name);
        }
      });
    },
    isStandard(item) {
      return item?.cash_reference?.reference_type?.value === 0;
    },
    freeSearch() {
      this.$emit("manualFetch");
    },
    onAddNewExpCashEntry() {
      this.$router.push({
        name: `${this.resource}.create`,
        params: {
          /* remove the / character if it is the first character of a string */
          returnUrl: this.$route.path.replace(/^\/(.*)/, "$1"),
        },
      });
    },
    // Le etichette delle righe dei saldi stanno nella colonna prima di
    // Entrate, allineate a destra verso gli importi.
    labelIndex(fields) {
      return fields.findIndex((field) => field.key === "entrate") - 1;
    },
    balanceCellClass(i, fields) {
      const label = this.labelIndex(fields);
      return { "balance-label": i === label, "text-right": i > label };
    },
    // Un saldo positivo va nella colonna Entrate, uno negativo in Uscite.
    balanceIn(value) {
      return value > 0 ? toLocaleCurrency(value) : "";
    },
    balanceOut(value) {
      return value < 0 ? toLocaleCurrency(Math.abs(value)) : "";
    },
    totalBalance() {
      return (
        Math.abs(parseFloat(this.current_balance.entrate)) -
        Math.abs(parseFloat(this.current_balance.uscite)) +
        Math.abs(parseFloat(this.previous_balance.entrate)) -
        Math.abs(parseFloat(this.previous_balance.uscite))
      );
    },
    ...mapActions("filters", {
      // saveFilterByName: "saveByName",
      removeFilterByName: "removeByName",
    }),
    ...mapActions("pagination", {removePaginationByName: "removeByName"}),
  },
  props: {
    type: {
      type: String,
      default() {
        return "";
      },
    },
    previous_balance_date: {
      type: String,
      default() {
        return "";
      },
    },
    current_balance_date: {
      type: String,
      default() {
        return "";
      },
    },
    previous_balance: {
      type: Object,
      default() {
        return {};
      },
    },
    current_balance: {
      type: Object,
      default() {
        return {};
      },
    },
    cash_reference_full_code: {
      type: Object,
      default() {
        return {};
      },
    },
    performedSearch: {
      type: Boolean,
      default: false,
    },
    filterCount: {
      type: Number,
      default() {
        return 0;
      },
    },
    isTableInModal: {
      type: Boolean,
      default: false,
    },
    saveRelationBtn: {
      type: Boolean,
      default: false,
    },
    hasChecksPaginated: {
      type: Boolean,
      default: false,
    },
    newExpCashEntryBtn: {
      type: Boolean,
      default: false,
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/css/table-mixins";

// Saldo iniziale in prima riga: più leggero dei movimenti.
.balance-row {
  background: white !important;
  color: var(--color-text-muted);
  font-style: italic;
}

// Totale movimenti e saldo finale.
.totals-row {
  background-color: var(--color-total-bg);
  padding-block: 8px;
  font-weight: 600;
}

.totals-row--final {
  font-weight: 700;
}

// Etichette a destra, verso gli importi, nel carattere della tabella.
.balance-label {
  text-align: right;
}

.balance-row.text-right,
.totals-row.text-right {
  --font-family: var(--font-family-num);
}

// Allineato alla tabella, che ha lo stesso rientro (div:has(> table) in
// custom.scss).
.balance-summary {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin: 0 0 12px calc(1.25rem - 4px);

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.balance-summary__tile {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 16px;
  background: white;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
}

.balance-summary__tile--final {
  border-top: 3px solid var(--lisaweb);
}

.balance-summary__label {
  font-size: var(--font-size-sm);
  color: var(--color-text-muted);
}

.balance-summary__value {
  --font-family: var(--font-family-num);
  font-size: var(--font-size-lg);
  color: var(--color-text);

  &.is-positive {
    color: var(--color-success);
  }

  &.is-negative {
    color: var(--status-ko);
  }
}
</style>
